/*
 * PetNGo bespoke header (surface: chrome) — interaction layer.
 *
 * Liquid port of the behavior baked into the Hydrogen header React
 * components:
 *   - MegaMenu.tsx          → .pn-megamenu-wrap (hover/focus open, leave
 *                             delay, Esc + click-outside + route close)
 *   - nav/SpeciesMegaMenu   → .pn-petmenu-wrap (same, plus the darkened
 *                             scrim, the desktop species→subcategory
 *                             swap, and the mobile full-screen drill-in)
 *   - HamburgerButton       → .pn-header-mobile-drawer (off-canvas)
 *
 * Search overlay interactions stay in petngo-chrome.js (the trigger +
 * overlay markup is inlined into the header but keyed by the same
 * data attributes that file already wires up).
 *
 * No framework, no mutation of shared globals — every handler is scoped
 * to the elements it owns. Idempotent: safe to call init() again on
 * shopify:section:load when the merchant edits the header in the editor.
 */
(function () {
  'use strict';

  var HOVER_CLOSE_DELAY_MS = 120;
  // Matches the header CSS: .pn-nav shows / .pn-hamburger hides at ≥768px,
  // so the hover mega-menus are the active nav from 768px up.
  var DESKTOP_MIN = 768;

  function isDesktop() {
    return window.matchMedia('(min-width: ' + DESKTOP_MIN + 'px)').matches;
  }

  /* ----------------------------------------------------------------
   * Generic hover/focus dropdown (mega-menu + species pet-menu share
   * the same open/close lifecycle; only the panel selector differs).
   * ---------------------------------------------------------------- */
  function wireDropdown(wrap, opts) {
    var panelSelector = opts.panelSelector;
    var triggerSelector = opts.triggerSelector;
    var scrimSelector = opts.scrimSelector || null;
    var onOpen = opts.onOpen || null;

    var panel = wrap.querySelector(panelSelector);
    var trigger = wrap.querySelector(triggerSelector);
    if (!panel || !trigger) return;
    var scrim = scrimSelector ? wrap.querySelector(scrimSelector) : null;

    var closeTimer = null;
    var isOpen = false;

    function applyOpen(next) {
      isOpen = next;
      panel.classList.toggle('is-open', next);
      panel.setAttribute('aria-hidden', next ? 'false' : 'true');
      trigger.setAttribute('aria-expanded', next ? 'true' : 'false');
      if (scrim) scrim.classList.toggle('is-open', next);
      if (next && onOpen) onOpen();
    }

    function cancelClose() {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    }
    function open() {
      cancelClose();
      if (!isOpen) applyOpen(true);
    }
    function close() {
      cancelClose();
      if (isOpen) applyOpen(false);
    }
    function scheduleClose() {
      cancelClose();
      closeTimer = setTimeout(function () {
        applyOpen(false);
        closeTimer = null;
      }, HOVER_CLOSE_DELAY_MS);
    }

    // Hover (desktop only — touch/mobile uses the drawer / drill-in).
    wrap.addEventListener('mouseenter', function () {
      if (isDesktop()) open();
    });
    wrap.addEventListener('mouseleave', function () {
      if (isDesktop()) scheduleClose();
    });

    // Focus-within for keyboard users.
    wrap.addEventListener('focusin', function () {
      if (isDesktop()) open();
    });
    wrap.addEventListener('focusout', function (event) {
      var next = event.relatedTarget;
      if (next && wrap.contains(next)) return;
      if (isDesktop()) scheduleClose();
    });

    // Click toggles (works on desktop for parent-only triggers that
    // shouldn't navigate; the markup makes those <button>s).
    trigger.addEventListener('click', function (event) {
      var isButton = trigger.tagName === 'BUTTON';
      if (isButton) {
        event.preventDefault();
        if (isOpen) {
          close();
        } else {
          open();
        }
      }
    });

    if (scrim) {
      scrim.addEventListener('click', close);
    }

    // Esc + click-outside dismissal.
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen) close();
    });
    document.addEventListener('mousedown', function (event) {
      if (!isOpen) return;
      if (!wrap.contains(event.target)) close();
    });

    return {open: open, close: close};
  }

  /* ----------------------------------------------------------------
   * Species pet-menu: desktop species→subcategory column swap.
   * ---------------------------------------------------------------- */
  function wireSpeciesSwap(wrap) {
    var links = wrap.querySelectorAll('[data-species-id]');
    var panels = wrap.querySelectorAll('[data-species-panel]');
    if (!links.length || !panels.length) return;

    function activate(id) {
      for (var i = 0; i < links.length; i++) {
        links[i].classList.toggle(
          'is-active',
          links[i].getAttribute('data-species-id') === id,
        );
      }
      for (var j = 0; j < panels.length; j++) {
        var match = panels[j].getAttribute('data-species-panel') === id;
        panels[j].hidden = !match;
      }
    }

    for (var k = 0; k < links.length; k++) {
      (function (link) {
        var id = link.getAttribute('data-species-id');
        link.addEventListener('mouseenter', function () {
          activate(id);
        });
        link.addEventListener('focus', function () {
          activate(id);
        });
      })(links[k]);
    }
  }

  /* ----------------------------------------------------------------
   * Off-canvas mobile drawer (hamburger).
   * ---------------------------------------------------------------- */
  function wireMobileDrawer(root) {
    var drawer = root.querySelector('[data-pn-mobile-drawer]');
    var openBtns = root.querySelectorAll('[data-pn-drawer-open]');
    if (!drawer || !openBtns.length) return;
    var scrim = drawer.querySelector('[data-pn-drawer-scrim]');
    var closeBtn = drawer.querySelector('[data-pn-drawer-close]');

    function open() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    for (var i = 0; i < openBtns.length; i++) {
      openBtns[i].addEventListener('click', open);
    }
    if (scrim) scrim.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
      }
    });

    // Accordion rows inside the drawer (native <details> handles most;
    // this just makes the whole summary toggle on click for the chevron).
  }

  function initRoot(root) {
    if (root.getAttribute('data-pn-header-init') === '1') return;
    root.setAttribute('data-pn-header-init', '1');

    // Mega-menus (Mascota / Marcas / Ayuda / etc.).
    var megaWraps = root.querySelectorAll('.pn-megamenu-wrap');
    for (var i = 0; i < megaWraps.length; i++) {
      wireDropdown(megaWraps[i], {
        panelSelector: '.pn-megamenu',
        triggerSelector: '.pn-megamenu-trigger',
      });
    }

    // Species pet-menu ("Comprar por mascota"). Desktop-only panel; on
    // mobile the species rows live in the off-canvas drawer.
    var petWrap = root.querySelector('.pn-petmenu-wrap');
    if (petWrap) {
      wireDropdown(petWrap, {
        panelSelector: '.pn-petmenu',
        triggerSelector: '.pn-petmenu-trigger',
        scrimSelector: '.pn-petmenu-scrim',
      });
      wireSpeciesSwap(petWrap);
    }

    wireMobileDrawer(root);
  }

  function init() {
    var roots = document.querySelectorAll('[data-pn-header]');
    for (var i = 0; i < roots.length; i++) initRoot(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
